import React, { useMemo, useState } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { cn, formatDate2 } from '@lib/utils.ts';
import { LineDot } from 'recharts/types/cartesian/Line';
import { BalanceStackedData } from '@src/types';

type StackPayload = {
  value: number;
  payload?: BalanceStackedData;
  color?: string;
};

const AMOUNT_UNIT = 1000;

interface TooltipProps {
  active?: boolean;
  payload?: StackPayload[];
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    return (
      <div className={'bg-[#222] p-2.5 rounded-1 text-xs'}>
        <p>{`${t('dashboard.dateTooltip')} ${formatDate2(payload[0].payload?.date)}`}</p>
        <p>{`Account Balance: ${payload[0]?.payload?.account_balance}`}</p>
        <p>{`Net P/L: ${payload[0]?.payload?.net_pl}`}</p>
        <p>{`Other: $${Number(payload[0]?.payload?.other) * AMOUNT_UNIT}`}</p>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  className?: string;
  payload?: StackPayload[];
  onClick: (key: any) => void;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  className,
  payload,
  onClick,
}) => {
  if (!payload) return null;

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <p className={'text-xs font-medium'}>
        Daily Net Profit/Loss vs Account Balance
      </p>
      <div className={'flex gap-x-3 p-2.5'}>
        {payload.map((entry, index) => (
          <div
            key={index}
            className={'flex items-center gap-x-1.5'}
            onClick={() => onClick(entry)}
          >
            <div
              className={`w-9 h-3`}
              style={{ backgroundColor: entry.color }}
            />
            <p className={'text-xs font-medium'}>{entry.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface DailyBalanceChartProps {
  data: BalanceStackedData[];
  height?: number;
  barSize?: number;
  legendClassName?: string;
  className?: string;
  dot?: LineDot;
  angle?: number;
}

export const DailyBalanceChart: React.FC<DailyBalanceChartProps> = ({
  data,
  height = 500,
  barSize = 36,
  className,
  legendClassName,
  dot = { r: 4, fill: '#8A8A8A' },
  angle,
}) => {
  // const isOther = useMemo(() => data.some((item) => item.other), [data]);

  const [visible, setVisible] = useState({
    account_balance: true,
    net_pl: true,
    other: true,
  });

  const toggle = (dataKey: any) => {
    setVisible((prev: any) => ({
      ...prev,
      [dataKey.dataKey]: !prev[dataKey.dataKey],
    }));
  };

  const filteredData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      account_balance: visible.account_balance ? item.account_balance : 0,
      net_pl: visible.net_pl ? item.net_pl : 0,
      other: visible.other ? item.other : undefined,
    }));
  }, [data, visible]);

  return (
    <ResponsiveContainer
      className={className}
      height={height}
      style={{ margin: 0, padding: 0 }}
      width="100%"
    >
      <ComposedChart data={filteredData} stackOffset="sign">
        <CartesianGrid stroke="#CECECE" strokeDasharray="0" vertical={false} />
        <XAxis
          angle={angle}
          dataKey="date"
          dy={10}
          fontSize={8}
          padding={{ left: 0, right: 0 }}
          stroke="#CECECE"
          tickFormatter={(value) => formatDate2(value)}
          tickLine={false}
        />
        <YAxis fontSize={8} stroke="#CECECE" tickLine={false} yAxisId="left" />
        <YAxis
          domain={[0, 'auto']}
          fontSize={8}
          orientation="right"
          stroke="#CECECE"
          tickFormatter={(value) => `$${value * AMOUNT_UNIT}`}
          tickLine={false}
          yAxisId="right"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          align="center"
          content={
            <CustomLegend className={legendClassName} onClick={toggle} />
          }
          layout="horizontal"
          verticalAlign="top"
          onMouseEnter={(e) => toggle(e.dataKey)}
        />
        <Bar
          barSize={barSize}
          dataKey="net_pl"
          fill="#C72E2E"
          name={'Net P/L'}
          stackId="a"
          yAxisId="left"
        >
          {filteredData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.net_pl >= 0 ? '#14CA74' : '#C72E2E'}
            />
          ))}
        </Bar>
        <Line
          dataKey="account_balance"
          dot={dot}
          name={'Account Balance'}
          stroke="#8A8A8A"
          strokeWidth={2}
          type="monotone"
          yAxisId="right"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
